import React, { useState, useRef, useEffect } from 'react';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import { colours } from '../../theme/constants';
import { useAuth0 } from '@auth0/auth0-react';
import Menu from '@mui/material/Menu';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import ListItemButton from '@mui/material/ListItemButton';
import SignalCellularAltOutlinedIcon from '@mui/icons-material/SignalCellularAltOutlined';
import ListItemIcon from '@mui/material/ListItemIcon';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Collapse from '@mui/material/Collapse';
import ListSubheader from '@mui/material/ListSubheader';
import { Link } from 'react-router';
import ToggleOnRoundedIcon from '@mui/icons-material/ToggleOnRounded';
import LogoutIcon from '@mui/icons-material/Logout';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import SuperAdminIcon from '../../svgIcons/SuperAdminIcon';
import useSuspenseFetch from '../../hooks/useSuspenseFetch';
import { usePutMutation } from '../../hooks/useAuthenticatedMutation';

const iconButton = {
    color: '#FFFFFF',
    '&:hover': {
        color: '#FFFFFF',
        backgroundColor: colours.secondary
    }
};

const CustomListItemButton = ({ to, icon: Icon, primaryText, onClick, divider = false }) => (
    <ListItem
        disablePadding
        onClick={onClick}
        divider={divider}
        sx={divider ? {
            paddingBottom: 1,
            marginBottom: 1
        } : undefined}
    >
        <ListItemButton
            component={to ? Link : 'div'}
            to={to}
            sx={{
                py: Icon ? 0 : 0.7,
                ml: Icon ? 0 : 2
            }}
        >
            {Icon && (
                <ListItemIcon sx={{ minWidth: 'unset', pr: 2 }}>
                    <Icon color="primary" />
                </ListItemIcon>
            )}
            <ListItemText
                primary={(
                    <Typography variant="subtitle2" sx={{ fontSize: '0.875rem' }}>
                        {primaryText}
                    </Typography>
                )}
            />
        </ListItemButton>
    </ListItem>
);

const returnTo = 'https://www.newzapp.co.uk/email-marketing/logout-thank-you.php';

const AccountProfile = ({ handleFeatureDialog }) => {
    const { data: currentUser } = useSuspenseFetch('user');
    const { data: account } = useSuspenseFetch('account');
    const { name, emailAddress, avatarURL, accounts, permissions: userPermissions } = currentUser;
    const { customerId } = account;
    const putMutation = usePutMutation('user');
    const { user, logout } = useAuth0();
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [settingOpen, setSettingOpen] = useState(false);
    const isSuperAdmin = userPermissions.includes('manage:accounts', 'manage:campaigns', 'manage:templates', 'manage:notifications');

    const currentAccount = accounts.find(account => account.id === customerId);
    const availableAccounts = accounts.filter(account => account.id !== currentAccount.id);
    const sortedAccounts = availableAccounts.sort((a, b) => new Date(b.lastLoggedIn) - new Date(a.lastLoggedIn));
    const [openId, setOpenId] = useState(null);
    const [overflowId, setOverflowId] = useState(null);
    const vScrollRef = useRef(null);
    const hScrollRef = useRef(null);

    const handleClose = () => {
        setAnchorEl(null);
        setSettingOpen(false);
        setOpenId(null);
        setOverflowId(null);
    };

    const handleMouseEnter = (e, id) => {
        const el = e.currentTarget;
        const isOverflowing = el.scrollWidth > el.clientWidth;

        if (isOverflowing) {
            setOverflowId(id);
            setOpenId(id);
        }
        else {
            setOverflowId(null);
            setOpenId(null);
        }
    };

    const handleMouseLeave = () => {
        setOpenId(null);
    };

    const closeTooltip = () => {
        setOpenId(null);
        setOverflowId(null);
    };

    const handleClick = (event) => setAnchorEl(event.currentTarget);

    const displayName = name ? name : user.name;

    const handleSwitchAccount = customerId => putMutation.mutate({ customerId }, { onSuccess: () => window.location.assign('/') });

    const UserAvatar = props => {
        return (
            <Avatar
                {...props}
                src={avatarURL}
            >
                {displayName.substring(0, 1)}
            </Avatar>
        );
    };

    useEffect(() => {
        const scrollers = [vScrollRef.current, hScrollRef.current].filter(Boolean);
        const onScroll = () => closeTooltip();
        const onWheel = () => closeTooltip();
        const onTouch = () => closeTooltip();

        const onKey = (e) => {
            const k = e.key;

            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(k)) {
                closeTooltip();
            }
        };

        scrollers.forEach(el => {
            el.addEventListener('scroll', onScroll, { passive: true });
            el.addEventListener('wheel', onWheel, { passive: true });
            el.addEventListener('touchmove', onTouch, { passive: true });
        });

        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('wheel', onWheel, { passive: true });
        window.addEventListener('resize', onScroll);
        window.addEventListener('keydown', onKey);

        return () => {
            scrollers.forEach(el => {
                el.removeEventListener('scroll', onScroll);
                el.removeEventListener('wheel', onWheel);
                el.removeEventListener('touchmove', onTouch);
            });
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('wheel', onWheel);
            window.removeEventListener('resize', onScroll);
            window.removeEventListener('keydown', onKey);
        };
    }, []);

    return (
        <>
            <IconButton
                edge="end"
                sx={{ ...iconButton, p: 1 }}
                size="large"
                onClick={handleClick}
            >
                <UserAvatar sx={{ width: 32, height: 32 }} />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onScrollCapture={closeTooltip}
                onWheelCapture={closeTooltip}
                onTouchMoveCapture={closeTooltip}
                slotProps={{
                    paper: {
                        sx: {
                            width: 300,
                            scrollbarWidth: 'thin'
                        }
                    }
                }}
            >
                <ListItem sx={{ pb: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'stretch', width: '100%' }}>
                        <ListItemAvatar>
                            <Avatar
                                src={avatarURL}
                                sx={{ width: 55, height: 55 }}
                            >
                                {displayName.substring(0, 1)}
                            </Avatar>
                        </ListItemAvatar>
                        <Box sx={{ maxWidth: 270, ml: 2 }}>
                            <ListItemText
                                primary={(
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ wordWrap: 'break-word', padding: 0, lineHeight: 1.25 }}
                                    >
                                        {displayName}
                                    </Typography>
                                )}
                                secondary={(
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                        sx={{ wordWrap: 'break-word', padding: 0, lineHeight: 1.75 }}
                                    >
                                        {emailAddress}
                                    </Typography>
                                )}
                            />
                        </Box>
                    </Box>
                </ListItem>
                <Divider sx={{ my: 1 }} />
                <ListItem sx={{ py: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'stretch', width: '100%', ml: 1 }}>
                        <ListItemText
                            primary={currentAccount.name}
                            slotProps={{
                                primary: {
                                    style: {
                                        whitespace: 'nowrap',
                                        overflow: 'hidden',
                                        textoverflow: 'ellipsis'
                                    }
                                },
                                secondary: { component: Box }
                            }}
                            secondary={(
                                <>
                                    <Typography variant="body2" color="textSecondary">
                                        Customer ID: {currentAccount.id}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Type: {currentAccount.isInternalCommunications ? 'Internal' : 'External'}
                                    </Typography>
                                </>
                            )}
                        />
                    </Box>
                </ListItem>
                <Divider sx={{ my: 1 }} />
                {availableAccounts.length > 0 && (
                    <ListItem sx={{ pr: 0, pt: 0, pb: 0 }}>
                        <Box sx={{ alignItems: 'center', width: '100%' }}>
                            <Box sx={{ maxHeight: 250, maxWidth: 300, overflowY: 'auto', scrollbarWidth: 'thin' }} ref={vScrollRef}>
                                <Box sx={{ display: 'flex', position: 'sticky', top: 0, zIndex: 1, backgroundColor: 'white' }}>
                                    <PeopleOutlineIcon color="primary" />
                                    <ListSubheader component="div" sx={{ py: 0, lineHeight: 1.5, flexGrow: 1 }}>Switch Account</ListSubheader>
                                </Box>
                                <List dense={true}>
                                    <Box sx={{ display: 'flex', maxWidth: 300 }}>
                                        <Box sx={{ px: 1.5, py: 1 }}>
                                            <Divider orientation="vertical" flexItem sx={{ marginLeft: 0, height: '100%', width: 1 }} />
                                        </Box>
                                        <Box sx={{ ml: 1, flexGrow: 1, overflowX: 'auto', whiteSpace: 'nowrap', pr: 2 }}>
                                            {sortedAccounts.map((u) => (
                                                <ListItemButton key={u.id} sx={{ pt: 0, px: 2 }} onClick={() => handleSwitchAccount(u.id)}>
                                                    <ListItemText
                                                        primary={(
                                                            <Tooltip
                                                                title={u.name}
                                                                open={openId === u.id && overflowId === u.id}
                                                                onClose={() => setOpenId(null)}
                                                                disableInteractive
                                                                arrow
                                                                slotProps={{
                                                                    popper: { modifiers: [{ name: 'preventOverflow', enabled: true }] }
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="subtitle2"
                                                                    noWrap
                                                                    sx={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}
                                                                    onMouseEnter={(e) => handleMouseEnter(e, u.id)}
                                                                    onMouseLeave={handleMouseLeave}
                                                                >
                                                                    {u.name}
                                                                </Typography>
                                                            </Tooltip>
                                                        )}
                                                        secondary={(
                                                            <>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Customer ID: {u.id}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Type: {u.isInternalCommunications ? 'Internal' : 'External'}
                                                                </Typography>
                                                            </>
                                                        )}
                                                        slotProps={{
                                                            secondary: { component: Box }
                                                        }}
                                                    />
                                                </ListItemButton>
                                            ))}
                                        </Box>
                                    </Box>
                                </List>
                            </Box>
                        </Box>
                    </ListItem>
                )}
                {availableAccounts.length > 0 && <Divider sx={{ my: 1 }} />}
                <Box>
                    <ListItemButton
                        onClick={() => setSettingOpen(!settingOpen)}
                        sx={{ py: 0, '&:hover': { backgroundColor: 'transparent' } }}
                    >
                        <ListItemIcon sx={{ minWidth: 'unset', pr: 2 }}>
                            <SettingsOutlinedIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                            primary={<Typography variant="subtitle2">Account Management</Typography>}
                        />
                        {!settingOpen ? <KeyboardArrowDown /> : <KeyboardArrowUpIcon />}
                    </ListItemButton>
                    <Collapse in={settingOpen} timeout="auto" unmountOnExit>
                        <Box sx={{ display: 'flex' }}>
                            <Box sx={{ pl: 3.5, py: 1 }}>
                                <Divider orientation="vertical" flexItem sx={{ marginLeft: 0, height: 150, width: 1 }} />
                            </Box>
                            <Stack sx={{ width: '100%' }}>
                                <CustomListItemButton
                                    to="/account"
                                    primaryText="My Account"
                                    onClick={handleClose}
                                />
                                <CustomListItemButton
                                    to="/account/configuration"
                                    primaryText="Configuration"
                                    onClick={handleClose}
                                />
                                <CustomListItemButton
                                    to="/account/auditing"
                                    primaryText="Audit Trail"
                                    onClick={handleClose}
                                />
                                <CustomListItemButton
                                    to="/account/invoices"
                                    primaryText="Invoices"
                                    onClick={handleClose}
                                />
                            </Stack>
                        </Box>
                    </Collapse>
                </Box>
                <Divider sx={{ my: 1 }} />
                <CustomListItemButton
                    to="/account/products"
                    icon={SignalCellularAltOutlinedIcon}
                    primaryText="Usage & Products"
                    onClick={handleClose}
                    divider={true}
                />
                {isSuperAdmin && (
                    <CustomListItemButton
                        to="/superadmin"
                        icon={SuperAdminIcon}
                        primaryText="Super Admin"
                        onClick={handleClose}
                        divider={true}
                    />
                )}
                {(currentUser.userFeature.length > 0 || currentUser.feature.some(feature => feature.level === 1)) && (
                    <CustomListItemButton
                        icon={ToggleOnRoundedIcon}
                        primaryText="Feature Preview"
                        onClick={handleFeatureDialog}
                        divider={true}
                    />
                )}
                <CustomListItemButton
                    icon={LogoutIcon}
                    primaryText="Sign Out"
                    onClick={() => logout({ returnTo })}
                />
            </Menu>
        </>
    );
};

export default AccountProfile;
